import type { LayoutElement, VenueLayout } from "./layout.ts";

/**
 * Shared seed floor plan for "Smoke & Mirrors" — the frontend-first source of
 * truth until the API lands. All three apps import this. The owner editor
 * overlays edits in its own localStorage; staff / customer render this directly.
 *
 * UUIDs are static so the three apps agree on element identity.
 */

/** Demo branch id. For a live API this must match a real `branch.id`. */
const BRANCH_ID = "a0000000-0000-4000-8000-000000000001";

function table(
  id: string,
  code: string,
  label: string,
  cx: number,
  cy: number,
  opts: Partial<Pick<LayoutElement, "rotation">> & {
    shape?: "circle" | "rect";
    seats?: number;
    capacity?: number;
    w?: number;
    h?: number;
    status?: "available" | "occupied" | "reserved" | "disabled";
  } = {},
): LayoutElement {
  const shape = opts.shape ?? "circle";
  const w = opts.w ?? (shape === "circle" ? 84 : 132);
  const h = opts.h ?? (shape === "circle" ? 84 : 78);
  return {
    kind: "table",
    id,
    code,
    label,
    cx,
    cy,
    w,
    h,
    rotation: opts.rotation ?? 0,
    shape,
    seats: opts.seats ?? 4,
    capacity: opts.capacity ?? opts.seats ?? 4,
    status: opts.status ?? "available",
  };
}

const elements: LayoutElement[] = [
  // Backdrops
  {
    kind: "zone",
    id: "b0000000-0000-4000-8000-000000000001",
    label: "VIP",
    cx: 210,
    cy: 170,
    w: 360,
    h: 280,
    rotation: 0,
    fill: "var(--gold-500)",
  },
  {
    kind: "dancefloor",
    id: "b0000000-0000-4000-8000-000000000002",
    label: "Dance Floor",
    cx: 560,
    cy: 430,
    w: 300,
    h: 220,
    rotation: 0,
  },
  // Fixtures
  {
    kind: "stage",
    id: "c0000000-0000-4000-8000-000000000001",
    label: "Stage",
    cx: 560,
    cy: 80,
    w: 420,
    h: 96,
    rotation: 0,
  },
  {
    kind: "bar",
    id: "c0000000-0000-4000-8000-000000000002",
    label: "Bar",
    cx: 930,
    cy: 360,
    w: 96,
    h: 360,
    rotation: 0,
  },
  {
    kind: "entrance",
    id: "c0000000-0000-4000-8000-000000000003",
    label: "Entrance",
    cx: 560,
    cy: 668,
    w: 150,
    h: 44,
    rotation: 0,
  },
  // Tables — VIP (circles)
  table("d0000000-0000-4000-8000-000000000001", "T1", "T1", 120, 150, { seats: 4 }),
  table("d0000000-0000-4000-8000-000000000002", "T2", "T2", 300, 150, {
    seats: 6,
    w: 100,
    h: 100,
    status: "occupied",
  }),
  table("d0000000-0000-4000-8000-000000000003", "T3", "T3", 210, 300, {
    seats: 2,
    w: 68,
    h: 68,
    status: "reserved",
  }),
  // Booths along the left (rects)
  table("d0000000-0000-4000-8000-000000000004", "B1", "B1", 150, 470, {
    shape: "rect",
    seats: 6,
    capacity: 8,
  }),
  table("d0000000-0000-4000-8000-000000000005", "B2", "B2", 150, 580, {
    shape: "rect",
    seats: 6,
    capacity: 8,
    status: "occupied",
  }),
  // Floor tables around the dance floor (circles)
  table("d0000000-0000-4000-8000-000000000006", "T4", "T4", 760, 300, { seats: 4 }),
  table("d0000000-0000-4000-8000-000000000007", "T5", "T5", 760, 470, {
    seats: 4,
    status: "occupied",
  }),
  table("d0000000-0000-4000-8000-000000000008", "T6", "T6", 760, 600, {
    seats: 2,
    w: 68,
    h: 68,
  }),
  // Stage-front high-top
  table("d0000000-0000-4000-8000-000000000009", "H1", "H1", 400, 300, {
    seats: 3,
    w: 64,
    h: 64,
    status: "disabled",
  }),
];

export const seedLayout: VenueLayout = {
  id: "e0000000-0000-4000-8000-000000000001",
  branchId: BRANCH_ID,
  name: "Smoke & Mirrors",
  floor: { width: 1040, height: 720 },
  elements,
  updatedAt: "2026-06-06T00:00:00.000Z",
};
